import { ReactNode } from 'react';

import { styled, useMediaQuery } from '@mui/material';

import { ModalMode } from '@app-velocity/message-bus';

import { Box } from '../../Box';
import { Header } from './components/Header';
import { Content } from './components/Content';
import { Controls } from './components/Controls';
import { BackHeader } from './components/BackHeader';
import { ModalMenuProvider } from '../context/ModalMenuContext';
import { useModalWrapperContext } from '../context/useModalWrapperContext';
import { Sidebar } from './components/Sidebar';
import { Menu } from './components/Menu';
import { MenuElement } from './components/MenuElement';
import { MenuLabel } from './components/MenuLabel';

export type ModalProps = {
  children: ReactNode;
  horizontal?: boolean;
  defaultKey?: string;
  'data-testid'?: string;
};

const getModalHeight = (mode: ModalMode) => {
  switch (mode) {
    case 'fullscreen':
      return { minHeight: '100vh' };
    // @TODO - the magic number should be moved or the structure changed
    case 'subview':
      return { height: 'calc(100vh - 54px)' };
    case 'default':
      return { minHeight: '10vh' };
    case 'auto':
      return { minHeight: 'unset' };
  }
};

const getModalMinWidth = (mode: ModalMode, isLargerScreen: boolean) => {
  if (['auto'].includes(mode)) return 'auto';
  if (['fullscreen', 'subview'].includes(mode)) return '100vw';
  if (isLargerScreen) return '450px';

  return '95vw';
};

const StyledModalBox = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isLargerScreen', 'horizontal', 'mode'].includes(prop as string),
})<{
  mode: ModalMode;
  isLargerScreen: boolean;
  horizontal?: boolean;
}>(({ mode, isLargerScreen, horizontal }) => ({
  backgroundColor: 'var(--color-white)',
  outlineWidth: '0',
  minWidth: getModalMinWidth(mode, isLargerScreen),
  ...getModalHeight(mode),
  maxWidth: ['fullscreen', 'subview'].includes(mode) ? 'unset' : '95vw',
  maxHeight: ['fullscreen', 'subview'].includes(mode) ? 'unset' : '95vh',
  borderRadius: ['fullscreen', 'subview'].includes(mode) ? 0 : '8px',
  display: 'flex',
  justifyContent: 'stretch',
  alignItems: 'stretch',
  flexDirection: horizontal ? 'row' : 'column',
  overflow: 'auto',
  position: 'relative',
}));

export const Modal = ({
  children,
  horizontal,
  defaultKey,
  ...rest
}: ModalProps) => {
  const { mode } = useModalWrapperContext();
  const isLargerScreen = useMediaQuery('(min-width: 500px)');

  const content = (
    <StyledModalBox
      data-testid={rest['data-testid']}
      mode={mode}
      isLargerScreen={isLargerScreen}
      horizontal={horizontal}
    >
      {children}
    </StyledModalBox>
  );

  if (!defaultKey) {
    return content;
  }

  return (
    <ModalMenuProvider defaultKey={defaultKey}>{content}</ModalMenuProvider>
  );
};

Modal.Header = Header;
Modal.BackHeader = BackHeader;
Modal.Content = Content;
Modal.Controls = Controls;
Modal.Sidebar = Sidebar;
Modal.Menu = Menu;
Modal.MenuElement = MenuElement;
Modal.MenuLabel = MenuLabel;
